<script>
  import { onDestroy, onMount } from "svelte";
  import { Input, Label } from "sveltestrap";
  import {
    callQueue,
    dataInfo,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    currMenu,
    directContext,
    currentElementLockGroup,
  } from "../../editor/storage.js";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import ItemResponse from "../component/ItemResponse.svelte";
  import { deepcopy } from "../../cicero-fronto/utility.js";
  import { dTypeIcon } from "../../editor/component/data.js";
  import { getAugmentation } from "../../editor/exploration/augment.js";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let subMenu;
  let currInteractions;
  let currTooltips;
  let currIndex;
  function setValues() {
    if ($currentSpec.fronto?.interaction !== undefined)
      currInteractions = deepcopy($currentSpec.fronto?.interaction);
    else currInteractions = {};
    currTooltips =
      $currentSpec.fronto?.layer
        ?.map((layer, li) => {
          if (layer.tooltip) {
            let tooltip = deepcopy(layer.tooltip);
            tooltip.layerName = layer.name;
            tooltip.layerIndex = li;
            return tooltip;
          }
          return;
        })
        .filter((t) => t !== undefined) || [];
  }

  function setSubMenu(s, i) {
    subMenu = s;
    currIndex = i;
  }
  function cancelSubMenu() {
    subMenu = undefined;
    currIndex = undefined;
  }

  let newTooltipLayer;
  function createNewTooltip() {
    if (newTooltipLayer) {
      let rule = {
        category: "manual",
        description: "Create a new tooltip for " + newTooltipLayer,
        specifier: { role: "layer", id: newTooltipLayer },
        action: "add",
        option: {
          role: "tooltip",
          visibility: "on-mark",
          fields: [],
        },
      };
      let fields = [];
      $currentSpec.fronto?.layout?.row.forEach((f) => {
        fields.push(f.field);
      });
      $currentSpec.fronto?.layout?.column.forEach((f) => {
        fields.push(f.field);
      });
      $currentSpec.fronto?.layer?.forEach((layer) => {
        if (layer.name === newTooltipLayer) {
          Object.keys(layer.mark).forEach((enc) => {
            if (layer.mark[enc].field !== undefined)
              fields.push(layer.mark[enc].field);
          });
          layer.text?.forEach((text) => {
            if (text.field !== undefined && !fields.includes(text.field)) {
              fields.push(text.field);
            }
          });
        }
      });
      fields.forEach((field) => {
        rule.option.fields.push({ field });
      });

      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply({
          afterUpdateAll: () => {
            getAugmentation(
              "tooltip",
              rule,
              null,
              "new",
              $currentSpec,
              $unlockedSpecs
            );
          },
        });
        return queue;
      });

      currIndex = currTooltips.length;
    }
  }
  function updateInteraction(event, role, option) {
    let newValue;
    if (role === "zoompan" || role === "context") {
      newValue = event.target.checked || null;
    } else if (role === "context.height") {
      newValue = parseFloat(event.target.value);
    } else if (role.startsWith("filter.")) {
      let existing = option?.filter;
      if (existing) newValue = deepcopy(existing);
      else newValue = [];
      if (role.endsWith(".remove")) {
        newValue = newValue.filter((d) => d !== event);
      } else if (role.endsWith(".add")) {
        newValue.push(event.target.value);
      }
    } else if (role === "tooltip.remove") {
      newValue = null;
    } else if (role === "tooltip.visibility") {
      newValue = event.target.value;
    } else if (role === "tooltip.layer") {
      newValue = event.target.value;
    } else if (
      role.match(/^tooltip\.fields\.[0-9]+\.(field\.remove|format)$/) || role === "tooltip.fields.add"
    ) {
      let fi = role.split(".")[2];
      newValue = deepcopy(option.fields);
      if (role.endsWith(".field.remove")) {
        newValue.splice(fi, 1);
      } else if (role.endsWith(".format")) {
        newValue[fi].format = event.target.value;
      } else if (role.endsWith(".add")) {
        newValue.push({ field: event.target.value });
      }
    }

    if (newValue || newValue == null) {
      let rule;
      if (role === "zoompan") {
        rule = {
          description: newValue ? "Set zoom + pan" : "Unset zoom + pan",
          specifier: { role: "interaction" },
          action: newValue ? "add" : "remove",
          option: { interaction: "zoompan" },
        };
      } else if (role === "context") {
        rule = {
          description: newValue
            ? "Set a context view"
            : "Unset the context view",
          specifier: { role: "interaction" },
          action: newValue ? "add" : "remove",
          option: { interaction: "context" },
        };
      } else if (role === "context.height") {
        rule = {
          description: "Edit context view height",
          specifier: { role: "interaction" },
          action: "modify",
          option: { interaction: "context", height: newValue },
        };
      } else if (role.startsWith("filter.")) {
        rule = {
          description: "",
          specifier: { role: "interaction" },
          action: "modify",
          option: { interaction: "filter", field: newValue },
        };
        if (role.endsWith(".remove")) {
          rule.description = `Remove ${newValue} to filter`;
        } else if (role.endsWith(".add")) {
          rule.description = `Add ${newValue} to filter`;
        }
      } else if (role.startsWith("tooltip.")) {
        rule = {
          description: "",
          specifier: { role: "tooltip" },
        };
        if (option.layerName) {
          rule.description += option.layerName;
          rule.specifier.layer = { id: option.layerName };
        } else if (option.layerIndex !== undefined) {
          rule.description += "layer " + option.layerIndex;
          rule.specifier.layer = { index: option.layerIndex };
        }
        if (role === "tooltip.remove") {
          rule.description = "Remove tooltip for " + rule.description;
          rule.action = "remove";
        } else if (role === "tooltip.visibility") {
          rule.description =
            "Modify tooltip visibility for " + rule.description;
          rule.action = "modify";
          if (newValue === "fixed") {
            rule.option = { external: true, fixed: true, hidden: false };
          } else if (newValue === "on-mark") {
            rule.option = { external: false, fixed: false, hidden: false };
          } else if (newValue === "hidden") {
            rule.option = { external: false, fixed: false, hidden: true };
          }
        } else if (role === "tooltip.layer") {
          rule.description = "Replace tooltip layer for " + rule.description;
          rule.action = "replace";
          rule.option = {
            to: {
              layer: {
                id: newValue,
              },
            },
          };
        } else if (
          role.match(/^tooltip\.fields\.[0-9]+\.(field\.remove|format)$/)  || role === "tooltip.fields.add"
        ) {
          console.log(newValue);
          rule.description = "Modify tooltip content for " + rule.description;
          rule.action = "modify";
          rule.option = { fields: newValue };
        }
      }

      if (rule) {
        rule.category = "manual";
        callQueue.update((queue) => {
          queue.add([rule], $unlockedSpecs).apply();
          return queue;
        });
      }
    }
  }
  function updateLock(event, role, option) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role) {
        let rule;
        if (role === "zoompan") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " zoom + pan",
            specifier: {
              role: "interaction",
              interaction: "zoompan",
            },
            action: "lock",
            option: {
              zoompan: newValue,
            },
          };
        } else if (role === "context") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " the context view",
            specifier: {
              role: "interaction",
              interaction: "context",
            },
            action: "lock",
            option: {
              context: newValue,
            },
          };
        } else if (role === "tooltip" && option) {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " the tooltip ",
            specifier: {
              role: "tooltip",
            },
            action: "lock",
            option: {
              tooltip: newValue,
            },
          };
          if (option.layerName) {
            rule.description += "for " + option.layerName;
            rule.specifier.layer = { id: option.layerName };
          } else if (option.layerIndex !== undefined) {
            rule.description += "for layer " + option.layerIndex;
            rule.specifier.layer = { index: option.layerIndex };
          }
        } else if (role === "tooltip.position" && option) {
          rule = {
            category: "lock",
            description:
              (newValue ? "Lock" : "Unlock") + " the tooltip position",
            specifier: {
              role: "tooltip",
            },
            action: "lock",
            option: {
              position: newValue,
            },
          };
          if (option.layerName) {
            rule.description += "for " + option.layerName;
            rule.specifier.layer = { id: option.layerName };
          } else if (option.layerIndex !== undefined) {
            rule.description += "for layer " + option.layerIndex;
            rule.specifier.layer = { index: option.layerIndex };
          }
        }

        if (rule) {
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }

  // pipelines
  onMount(() => {
    setValues();
    subMenu = undefined;
    currIndex = undefined;
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    subMenu = undefined;
    currIndex = undefined;
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["interaction"]?.title || "Interaction"}
  </h2>
  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !subMenu}
    <!-- Tooltip -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <h3 class="h-sub">Tooltip</h3>
      </div>
    </div>
    {#if currTooltips}
      <div class="dupo-menu-pane mb-2">
        {#each currTooltips as tooltip, ti}
          <div class="dupo-menu-pane-form-row">
            <div class="menu-second-level menu-with-buttons">
              <div class="text-truncate" style="user-select:none;">
                <i class="bi bi-hand-index-thumb me-1" />{tooltip.layerName}
              </div>
              <div class="dupo-pane-list-button">
                <button
                  type="button"
                  on:click={() => {
                    setSubMenu("edit-tooltip", ti);
                  }}
                  ><i
                    class="bi bi-pencil-fill"
                    alt="{`Edit tooltip for ${tooltip.layerName}`}}"
                  /></button
                >
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-form-row">
        <button
          type="button"
          class="menu-second-level"
          on:click={() => {
            setSubMenu("edit-tooltip", "new");
          }}><i class="bi bi-plus-circle me-1" /> Add a new tooltip</button
        >
      </div>
    </div>

    <div class="pane-divider" />
    <!-- zoom + pan / context -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <h3 class="h-sub">Detail browse</h3>
      </div>
    </div>
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        title="Use zoom+pan"
        token="zoompan"
        type="inline-check"
        {inputFocusAction}
        {inputBlurAction}
        value={currInteractions.zoompan}
        changeAction={(event) => {
          updateInteraction(event, "zoompan", currInteractions);
        }}
      />
    </div>

    <!-- lock -->
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        type="lock"
        tooltipMessage="Prevent suggesting responsive alternatives that add or remove zoom + pan. Element-Lock applies to its source or target design(s)."
        title="Lock zoom + pan"
        token={"zoom-lock"}
        value={currInteractions.lock?.zoompan}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateLock(event, "zoompan", currInteractions);
        }}
      />
    </div>

    <div class="pane-divider" />
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        title="Use a context view"
        token="context"
        type="inline-check"
        {inputFocusAction}
        {inputBlurAction}
        value={currInteractions.context}
        changeAction={(event) => {
          updateInteraction(event, "context", currInteractions);
        }}
      />
    </div>
    {#if currInteractions.context !== undefined}
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          title="Context view height"
          token="context-height"
          type="height"
          {inputFocusAction}
          {inputBlurAction}
          value={currInteractions.context?.height}
          changeAction={(event) => {
            updateInteraction(event, "context.height", currInteractions);
          }}
        />
      </div>
    {/if}
    <!-- lock -->
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        type="lock"
        tooltipMessage="Prevent suggesting responsive alternatives that make changes to the context view. Element-Lock applies to its source or target design(s)."
        title="Lock the context view"
        token={"context-lock"}
        value={currInteractions.lock?.context}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateLock(event, "context", currInteractions);
        }}
      />
    </div>
    <div class="dupo-menu-pane mb-5">
      <div class="dupo-menu-pane-row">
        <p class="description">
          Using zoom+pan and a context view cancels out each other.
        </p>
      </div>
    </div>
  {:else if subMenu === "edit-tooltip"}
    {#if currIndex === "new"}
      <!-- new -->
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <p class="description">Select a layer to add a new tooltip.</p>
        </div>
      </div>
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="layer">Layer</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            name="layer"
            id="layer"
            type="select"
            class="custom-form-select"
            bind:value={newTooltipLayer}
          >
            <option value={undefined} />
            {#each $currentSpec.fronto.layer as layer}
              {#if layer.name !== undefined && layer.tooltip === undefined}
                <option value={layer.name}
                  >{layer.name} / {layer.mark?.type}</option
                >
              {/if}
            {/each}
          </Input>
        </div>
        <div class="dupo-menu-pane-button-row mt-2">
          <button
            type="button"
            on:click={() => {
              createNewTooltip();
            }}
          >
            <i class="bi bi-plus-circle me-1" /> Add a new tooltip
          </button>
        </div>
      </div>
    {:else if currTooltips[currIndex] !== undefined}
      <!-- edit -->
      <!-- Layer -->
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="layer">Layer</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            name="layer"
            id="layer"
            type="select"
            class="custom-form-select"
            value={currTooltips[currIndex].layerName}
            on:change={(event) => {
              updateInteraction(
                event,
                "tooltip.layer",
                currTooltips[currIndex]
              );
              if ($directContext) currMenu.set("selection");
              else cancelSubMenu();
            }}
          >
            <option value={undefined} />
            {#each $currentSpec.fronto.layer as layer}
              {#if layer.name !== undefined}
                <option value={layer.name}
                  >{layer.name} / {layer.mark?.type}</option
                >
              {/if}
            {/each}
          </Input>
        </div>
      </div>
      <!-- visibility -->
      <div class="dupo-menu-pane mb-4">
        <div class="dupo-menu-pane-row">
          <Label for="visibility">Visibility</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            name="layer"
            id="layer"
            type="select"
            class="custom-form-select"
            value={currTooltips[currIndex].visibility}
            on:change={(event) => {
              updateInteraction(
                event,
                "tooltip.visibility",
                currTooltips[currIndex]
              );
            }}
          >
            <option value={undefined} />
            <option value="on-mark">On the mark</option>
            <option value="fixed">Fixed at the bottom</option>
            <option value="hidden">Hidden</option>
          </Input>
        </div>
      </div>

      <!-- fields -->
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <h3 class="h-sub">Contents</h3>
        </div>
      </div>
      {#if currTooltips[currIndex].fields?.constructor.name === "Array"}
        {#each currTooltips[currIndex].fields as f, fi}
          <div class="dupo-menu-pane mb-2">
            <div class="dupo-menu-pane-row">
              <div class="menu-second-level menu-with-buttons">
                <div class="text-truncate pe-2" style="user-select:none;">
                  <Label class="mb-0" style="font-size: 0.8rem;"
                    >Field: <span
                      class="custom-form-select d-inline-block ms-1"
                      style="width: auto;"
                      ><i
                        class={`bi bi-${
                          dTypeIcon[
                            $dataInfo.stats?.[f.field]?.type || "quantitative"
                          ]
                        } me-1`}
                      />{f.field}</span
                    >
                  </Label>
                </div>
                <div class="dupo-pane-list-button">
                  <button
                    type="button"
                    value={f.field}
                    on:click={(event) => {
                      updateInteraction(
                        event,
                        `tooltip.fields.${fi}.field.remove`,
                        currTooltips[currIndex]
                      );
                    }}
                    ><i
                      class="bi bi-eraser"
                      alt="{`Remove ${f.field} for the tooltip`}}"
                    /></button
                  >
                </div>
              </div>
            </div>
            <div class="dupo-menu-pane-form-row">
              <ItemResponse
                type="text"
                token={"format-" + fi}
                title="Format string"
                {inputFocusAction}
                {inputBlurAction}
                value={f.format || ""}
                changeAction={(event) => {
                  updateInteraction(
                    event,
                    `tooltip.fields.${fi}.format`,
                    currTooltips[currIndex]
                  );
                }}
              />
            </div>
          </div>
          <div class="pane-divider" />
        {/each}
      {/if}

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <i class="bi bi-plus-circle me-1" /> Add a new line
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="select"
            class="custom-form-select"
            on:change={(event) => {
              console.log("...")
              updateInteraction(
                event,
                "tooltip.fields.add",
                currTooltips[currIndex]
              );
            }}
          >
            <option value={undefined} />
            {#each $dataInfo.columns as c}
              <option value={c}>
                <i
                  class={`bi bi-${
                    dTypeIcon[$dataInfo.stats?.[c]?.type] || ""
                  } me-1`}
                />
                {c}
              </option>
            {/each}
          </Input>
        </div>
      </div>

      <!-- lock -->
      <div class="pane-divider" />
      <div class="dupo-menu-pane mb-2 pt-2">
        <ItemResponse
          type="lock"
          tooltipMessage="Prevent suggesting responsive alternatives that make changes to this tooltip. Element-Lock applies to its source or target design(s)."
          title="Lock tooltip"
          token={"tooltip-lock"}
          value={currTooltips[currIndex].lock}
          {inputFocusAction}
          {inputBlurAction}
          changeAction={(event) => {
            updateLock(event, "tooltip", currTooltips[currIndex]);
          }}
        />
        <div class="dupo-menu-pane-row">
          <p class="text-sm">Does not apply to the tooltip position.</p>
        </div>
      </div>
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          type="lock"
          tooltipMessage="Prevent suggesting responsive alternatives that reposition this tooltip. Element-Lock applies to its source or target design(s)."
          title="Lock tooltip position"
          token={"tooltip-position-lock"}
          value={currTooltips[currIndex].textLock}
          {inputFocusAction}
          {inputBlurAction}
          changeAction={(event) => {
            updateLock(event, "tooltip.position", currTooltips[currIndex]);
          }}
        />
      </div>

      <!-- removal -->
      <div class="pane-divider mt-2" />
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-button-row">
          <button
            type="button"
            on:click={(event) => {
              updateInteraction(
                event,
                "tooltip.remove",
                currTooltips[currIndex]
              );
              if ($directContext) currMenu.set("selection");
              else cancelSubMenu();
            }}
          >
            <i class="bi bi-eraser me-1" /> Delete this tooltip
          </button>
        </div>
      </div>
    {/if}

    <div class="pane-divider" />
    <div class="dupo-menu-pane-button-row">
      <button
        type="button"
        on:click={() => {
          if ($directContext) currMenu.set("selection");
          else cancelSubMenu();
        }}
      >
        <i class="bi bi-arrow-left me-1" /> Go back
      </button>
    </div>
  {/if}
</section>
